<template>
  <!--
    The info affordance next to a KPI label. Hover and keyboard focus open it on
    desktop; reka-ui's tooltip never opens on touch (its trigger ignores touch
    pointer events), so the trigger toggles `isOpen` on click as well. That is
    also why `disableClosingTrigger` is set — without it reka would close the
    tooltip on the same tap that opened it. Tapping outside or pressing Escape
    dismisses it as normal.
  -->
  <TooltipProvider :delay-duration="100">
    <Tooltip v-model:open="isOpen" disable-closing-trigger>
      <TooltipTrigger
        as="button"
        type="button"
        :aria-label="`What does ${label} mean?`"
        class="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full opacity-80 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
        @click="isOpen = !isOpen"
      >
        <Icon name="Info" :class="iconClass" />
      </TooltipTrigger>
      <TooltipContent
        :side="side"
        :side-offset="8"
        :collision-padding="16"
        class="!bg-bg-canvas !text-strongest w-[17rem] rounded-2xl ring-1 ring-outline-dividers p-4 text-left shadow-lg sm:w-72"
      >
        <p class="mb-1 font-inter text-sm font-semibold text-strongest">{{ label }}</p>
        <p class="font-inter text-sm font-light leading-relaxed text-strong">{{ text }}</p>
        <TooltipArrow class="fill-bg-canvas" />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>

<script setup>
import { ref } from "vue";
import { Icon } from "@/components";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

defineProps({
  // The KPI this explains — doubles as the tooltip heading and the accessible
  // name of the trigger.
  label: { type: String, required: true },
  text: { type: String, required: true },
  side: { type: String, default: "top" },
  // Hero KPI labels use a 14px icon; larger headings pass their own size.
  iconClass: { type: String, default: "h-3.5 w-3.5" },
});

const isOpen = ref(false);
</script>
